import type { ReactElement } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { linkClick } from './routerLink';
import { TooltipActionItem } from './TooltipActionItem';

/**
 * A row action that is a real link — the same tooltip icon button as every
 * other grid action (see {@link TooltipActionItem}), rendered as an `<a href>`.
 *
 * Why a link rather than an `onClick`: an action that leads to another screen
 * should behave like every other link in the app, so middle click and Ctrl/Cmd
 * click open it in a new tab (see components/routerLink). A plain click still
 * navigates in-app.
 *
 * `TooltipActionItem` spreads what it does not use onto its `IconButton`, which
 * is polymorphic; MUI's prop types do not model that, which is the one cast
 * below.
 */
export default function GridActionLink({
    to,
    icon,
    label
}: {
    /** In-app route the action leads to. */
    to: string;
    icon: ReactElement;
    /** The tooltip, and the button's accessible name. */
    label: string;
}) {
    const navigate = useNavigate();
    const asRouterLink = { component: RouterLink, to } as object;
    return (
        <TooltipActionItem
            icon={icon}
            label={label}
            color="inherit"
            onClick={linkClick(() => navigate(to))}
            {...asRouterLink}
        />
    );
}
